import { useQuery, useQueryClient, useQueries } from "@tanstack/react-query";
import { useEffect, useRef } from "react";
import { useSnackbar } from "notistack";

import { useLoadingScreen } from '../../../Store/LoadingManager';

import QueryBuilderHelper from "../../../Utilities/Helpers/QueryBuilderHelper";
import DropdownOptionsHelper from "../../../Utilities/Helpers/DropdownOptionsHelper";
import GlobalFunctionHelper from "../../../Utilities/Helpers/GlobalFunctionHelper";

import { CachingEnum } from '../../../Utilities/Enums/CachingEnum';
import { CurrencyEnum } from '../../../Utilities/Enums/CurrencyEnums';

import { GetPriceSettings, GetModulesPriceSettings, GetStoragesPriceSettings, GetUserTiersPriceSettings, CreatePriceSettingService } from "../../../Api/Service/Maintenance/PriceSettings/priceSettingsService";

import { ErrorMessage, SuccessMessage } from "../../../Constants/Messages";
import { LegendItemInterface } from "../../../Models/Common/List/LegendInterface";
import { ActiveStatus, ActiveStatusEnum } from "../../../Utilities/Enums/StatusEnums";

export function usePriceSettingsHook(q_queryKey: any, currencyCode: CurrencyEnum = GlobalFunctionHelper.getCurrencyCode(), runInitialFetch: boolean = true) {
    const queryClient = useQueryClient();
    const { enqueueSnackbar } = useSnackbar();
    const DropdownList = [CachingEnum.PRICE_SETTINGS_TAB_LIST];
    let { loading, setLoading } = useLoadingScreen();

    // Initial fetch
    const { data, fetchStatus } = useQuery({
        queryKey: q_queryKey,
        queryFn: () => getPriceSettings({currencyCode: currencyCode}),
        enabled: runInitialFetch,
    });

    const { data: DropdownData, pending: dropdownFetchStatus } = useQueries({
        queries: DropdownList.map((item) => {
            return {
                queryKey: [item],
                queryFn: () => DropdownOptionsHelper.getLists(item),
            }
        }),
        combine: (results) => {
            const dictionary = Object.assign({}, ...results.map((x, index) => ({ [DropdownList[index]]: x.data })));
            return {
                data: dictionary,
                pending: results.some((result) => result.isPending),
            }
        },
    })

    const getPriceSettings = async (params: any) => {
        setLoading(true);

        let _data;
        let _legends;
        const responseData = await GetPriceSettings(QueryBuilderHelper.BuildQueryString(params));

        if (responseData?.isSuccess) {
            _data = responseData?.data
        } else {
            enqueueSnackbar(responseData?.message ?? (responseData?.data?.message ?? ErrorMessage.PROBLEM_FETCHING_DATA), { variant: 'error' })
        }

        setLoading(false);
        
        return { data: _data?.data}
    }

    const getModulesPriceSettings = async () => {
        setLoading(true);
        
        const responseData = await GetModulesPriceSettings();

        setLoading(false);
        
        return responseData?.data ?? null;
    }

    const getStoragesPriceSettings = async () => {
        setLoading(true);
        
        const responseData = await GetStoragesPriceSettings();

        setLoading(false);
        
        return responseData?.data ?? null;
    }

    const getUserTiersPriceSettings = async () => {
        setLoading(true);
        
        const responseData = await GetUserTiersPriceSettings();

        setLoading(false);
        
        return responseData?.data ?? null;
    }

    const createPriceSettings = async (params: any) => {
        setLoading(true);
        const responseData = await CreatePriceSettingService(params);

        if (responseData?.isSuccess) {
            queryClient.invalidateQueries(q_queryKey);
            enqueueSnackbar(responseData?.data?.message ?? "Price Settings " + SuccessMessage.UPDATED_SUCCESSFULLY, { variant: 'success' })
        } else {
            enqueueSnackbar(responseData?.message ?? (responseData?.data?.message ?? ErrorMessage.PROBLEM_UPDATING_DATA), { variant: 'error' })
        }

        setLoading(false);

        return responseData;
    }

    return { data, DropdownData, fetchStatus, dropdownFetchStatus, getPriceSettings, createPriceSettings, getModulesPriceSettings, getStoragesPriceSettings, getUserTiersPriceSettings }
}